import { Check, Circle, CircleX, LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type CloudConversationStartupPhase,
  cloudConversationStartupSteps,
  formatStartupDuration,
} from '@/lib/conversations/cloud-conversation-startup';

type StartupStepId = Exclude<CloudConversationStartupPhase, 'error'>;

/** Cadence for refreshing the live duration readout while a phase is in flight. */
const DURATION_TICK_INTERVAL_MS = 500;

export function CloudConversationStartup({
  hasRepository,
  phase,
  title = 'Starting cloud conversation',
}: {
  hasRepository: boolean;
  phase: CloudConversationStartupPhase;
  title?: string;
}) {
  const steps = useMemo(() => cloudConversationStartupSteps(hasRepository), [hasRepository]);
  const [now, setNow] = useState(() => Date.now());
  const [durations, setDurations] = useState<Partial<Record<StartupStepId, number>>>({});
  const startupStartedAtRef = useRef(now);
  const trackedPhaseRef = useRef(phase);
  const lastOperationalPhaseRef = useRef<StartupStepId>(
    phase === 'error' ? (steps[0]?.id ?? 'task-configuration') : phase,
  );
  const phaseStartedAtRef = useRef(now);
  const displayedPhase = phase === 'error' ? lastOperationalPhaseRef.current : phase;
  const phaseIndex = steps.findIndex((step) => step.id === displayedPhase);
  const activeIndex = Math.max(0, phaseIndex);

  useEffect(() => {
    const currentTime = Date.now();
    const previousPhase = trackedPhaseRef.current;

    if (previousPhase !== phase) {
      const previousPhaseIndex = steps.findIndex((step) => step.id === previousPhase);
      const nextPhaseIndex = steps.findIndex((step) => step.id === phase);
      const restarted =
        previousPhase === 'error' ||
        (previousPhaseIndex >= 0 && nextPhaseIndex >= 0 && nextPhaseIndex < previousPhaseIndex);
      if (restarted) {
        setDurations({});
        startupStartedAtRef.current = currentTime;
      } else {
        setDurations((current) => ({
          ...current,
          [previousPhase]: currentTime - phaseStartedAtRef.current,
        }));
      }
      trackedPhaseRef.current = phase;
      if (phase !== 'error') lastOperationalPhaseRef.current = phase;
      phaseStartedAtRef.current = currentTime;
      setNow(currentTime);
    }
    // Does nothing unless the phase changed: the steps only say where each phase stands.
  }, [phase, steps]);

  const measuredDurations = steps.flatMap((step, index) => {
    const duration =
      durations[step.id] !== undefined
        ? durations[step.id]
        : activeIndex === index && phase !== 'error'
          ? now - phaseStartedAtRef.current
          : undefined;
    return duration === undefined ? [] : [{ id: step.id, duration }];
  });
  const bottleneck = measuredDurations.reduce<(typeof measuredDurations)[number] | undefined>(
    (slowest, measurement) =>
      slowest === undefined || measurement.duration > slowest.duration ? measurement : slowest,
    undefined,
  );

  useEffect(() => {
    if (phase === 'error') return;
    const interval = window.setInterval(() => setNow(Date.now()), DURATION_TICK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [phase]);

  return (
    <div
      aria-live="polite"
      className="mx-auto mb-5 w-full max-w-conversation rounded-xl border border-hairline-strong bg-surface-raised px-4 py-3.5 shadow-desktop-soft"
      role="status"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-medium text-sm">{title}</p>
        {phase === 'error' ? (
          <span className="text-destructive text-xs">Failed</span>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <span>
              {Math.max(1, activeIndex + 1)} / {steps.length}
            </span>
            <span aria-hidden="true">·</span>
            <span className="font-mono tabular-nums">
              {formatStartupDuration(now - startupStartedAtRef.current)} total
            </span>
          </div>
        )}
      </div>
      <ol className="grid gap-2.5">
        {steps.map((step, index) => {
          const complete =
            durations[step.id] !== undefined &&
            (phase === 'error' ? index < activeIndex : index <= activeIndex);
          const skipped = index < activeIndex && !complete;
          const failed = phase === 'error' && activeIndex === index;
          const active = phase !== 'error' && activeIndex === index;
          const duration =
            complete || failed
              ? durations[step.id]
              : active
                ? now - phaseStartedAtRef.current
                : undefined;
          return (
            <li
              className={`flex min-w-0 items-start gap-2 rounded-lg px-2 py-1.5 text-xs ${
                bottleneck?.id === step.id && duration !== undefined
                  ? 'bg-amber-500/8 ring-1 ring-amber-500/20 '
                  : ''
              }${active || failed ? 'text-foreground' : 'text-muted-foreground'}`}
              key={step.id}
            >
              {complete ? (
                <Check className="mt-0.5 size-3.5 shrink-0 text-desktop-success" />
              ) : active ? (
                <LoaderCircle className="mt-0.5 size-3.5 shrink-0 animate-spin" />
              ) : failed ? (
                <CircleX className="mt-0.5 size-3.5 shrink-0 text-destructive" />
              ) : (
                <Circle className="mt-0.5 size-3.5 shrink-0 opacity-45" />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5 font-medium">
                  {step.label}
                  {skipped && (
                    <span className="rounded-sm bg-muted px-1.5 py-0.5 font-normal type-caption">
                      Skipped
                    </span>
                  )}
                  {failed && (
                    <span className="rounded-sm bg-destructive/10 px-1.5 py-0.5 font-normal type-caption text-destructive">
                      Failed here
                    </span>
                  )}
                  {bottleneck?.id === step.id && duration !== undefined && (
                    <span className="rounded-sm bg-amber-500/12 px-1.5 py-0.5 font-normal type-caption text-amber-700 dark:text-amber-300">
                      {active ? 'Current bottleneck' : 'Slowest so far'}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-caption text-muted-foreground/80 leading-4">
                  {step.detail}
                </span>
              </span>
              {duration !== undefined && (
                <span className="shrink-0 font-mono text-caption tabular-nums text-muted-foreground">
                  {formatStartupDuration(duration)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
